import { NotificationSource } from '../enums/notification-source'

/**
 * Minimal feed sub-resource shape required to build attribution text.
 * Intentionally decoupled from `@pushly/models` so the helper stays pure and
 * trivially unit-testable.
 */
export interface IFeedAttributionRef {
    id?: number
    name?: string | null
}

/**
 * Input accepted by {@link getNotificationAttribution}. Callers map from either
 * `GetNotificationResponse`, `GetNotificationScheduleResponse`, or the mobile
 * `NotificationCardData` shape into this small, model-agnostic contract.
 */
export interface INotificationAttributionInput {
    /** Notification source (e.g. `NotificationSource.FEED`). */
    source?: string | null
    /** Resolved creator display name (e.g. `created_by_user_name`). */
    createdByName?: string | null
    /** Feed sub-resource, present when the notification originated from a feed. */
    feed?: IFeedAttributionRef | null
}

export enum NotificationAttributionKind {
    /** Feed-sourced notification with a resolvable feed name. */
    FeedResolved = 'feed-resolved',
    /** Feed-sourced notification without a resolvable feed name. */
    FeedUnresolved = 'feed-unresolved',
    /** Any non-feed source — falls back to creator attribution. */
    Creator = 'creator',
}

export interface INotificationAttribution {
    kind: NotificationAttributionKind
    /** True when the notification originates from a feed. */
    isFeed: boolean
    /**
     * Full, self-contained plain-text label:
     *   - `"Created by Feed: {name}"` — {@link NotificationAttributionKind.FeedResolved}
     *   - `"Created from Feed"`        — {@link NotificationAttributionKind.FeedUnresolved}
     *   - `"Created by {creator}"`     — {@link NotificationAttributionKind.Creator} with a name
     *   - `"Created by"`               — {@link NotificationAttributionKind.Creator} without a name
     *
     * Use this for accessible text (e.g. `title`/`aria-label`) or plain contexts.
     */
    text: string
    /**
     * Static, translatable prefix that precedes {@link value}. Render {@link value}
     * inside a `NoTranslate` wrapper so proper nouns are never machine-translated.
     */
    prefix: string
    /**
     * Dynamic, do-not-translate portion (feed name or creator name).
     * `undefined` when there is nothing dynamic to render (unresolved feed or
     * missing creator name).
     */
    value?: string
}

const FEED_RESOLVED_PREFIX = 'Created by Feed: '
const FEED_UNRESOLVED_TEXT = 'Created from Feed'
const CREATOR_PREFIX = 'Created by '

function isFeedSource(source?: string | null): boolean {
    return typeof source === 'string' && source.trim().toUpperCase() === NotificationSource.FEED
}

/** Returns the trimmed value when it holds visible characters, otherwise `undefined`. */
function presentOrUndefined(value?: string | null): string | undefined {
    if (typeof value !== 'string') return undefined
    return value.trim().length > 0 ? value.trim() : undefined
}

/**
 * Centralized, pure attribution resolver for notifications.
 *
 * Feed-sourced notifications are attributed to their originating feed; every
 * other source preserves the existing "Created by {creator}" behavior.
 */
export function getNotificationAttribution(input: INotificationAttributionInput): INotificationAttribution {
    if (isFeedSource(input.source)) {
        const feedName = presentOrUndefined(input.feed?.name)

        if (feedName !== undefined) {
            return {
                kind: NotificationAttributionKind.FeedResolved,
                isFeed: true,
                prefix: FEED_RESOLVED_PREFIX,
                value: feedName,
                text: `${FEED_RESOLVED_PREFIX}${feedName}`,
            }
        }

        return {
            kind: NotificationAttributionKind.FeedUnresolved,
            isFeed: true,
            prefix: FEED_UNRESOLVED_TEXT,
            value: undefined,
            text: FEED_UNRESOLVED_TEXT,
        }
    }

    const creator = presentOrUndefined(input.createdByName)

    return {
        kind: NotificationAttributionKind.Creator,
        isFeed: false,
        prefix: CREATOR_PREFIX,
        value: creator,
        text: creator !== undefined ? `${CREATOR_PREFIX}${creator}` : CREATOR_PREFIX.trimEnd(),
    }
}

/** Convenience wrapper returning only the full plain-text attribution label. */
export function getNotificationAttributionText(input: INotificationAttributionInput): string {
    return getNotificationAttribution(input).text
}
